import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  XMarkIcon,
  PlusIcon,
  BellIcon,
  EyeIcon,
  EyeSlashIcon,
  KeyIcon,
  TrashIcon,
  UserIcon,
  ShieldCheckIcon,
  LockClosedIcon,
  DocumentDuplicateIcon,
  CheckIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import {
  useCriarUsuario,
  useAtualizarUsuario,
  useAlterarStatus,
  useRedefinirSenha,
  useAlterarSenhaManual,
} from '../../hooks/useUsuarios';
import { usePermissoes } from '../../hooks/usePermissoes';
import { useFocusTrap } from '../../hooks/useFocusTrap';
import { useToast } from '../ui/Toast';
import { ToggleIris, BadgeEmBreve, AbasSublinhado, inputIris, btnPrimario, btnSecundario } from '../ui/iris';
import PermissoesEditor from './PermissoesEditor';
import { gerarSenhaSegura } from '../../utils/randomPassword';
import { extrairMensagemErro } from '../../utils/extrairMensagemErro';
import { logError } from '../../utils/clientLog';
import { maskTelefoneInput, unmaskDigits } from '../../utils/format';
import { formatDateTime } from '../../utils/format';
import type { Usuario } from '../../types/usuarios';

/**
 * UserDialog (handoff design_handoff_usuarios_dialogs) — diálogo único de
 * usuário. Modo 'new': formulário de criação. Modo 'edit': abas
 * Dados · Perfil de acesso (PermissoesEditor: modelos + granulares) ·
 * Segurança (senhas, só ADMIN). Substitui o antigo EditarUsuarioModal.
 *
 * Real: nome/email/fone, papel ADMIN|USER, ativar/suspender, permissões por
 * usuário com modelos de perfil, redefinir/definir senha.
 * Stub/TODO(backend): convite por email (criação e reset — backend NÃO envia
 * email; a senha gerada aparece num diálogo pra copiar), papéis RBAC
 * granulares (SISTEMA_PLANEJAMENTO.md), remoção definitiva (não há DELETE).
 */

type AbaEdit = 'dados' | 'permissoes' | 'seguranca';

interface UserDialogProps {
  mode: 'new' | 'edit';
  /** Obrigatório no modo edit. */
  usuario?: Usuario;
  isOpen: boolean;
  onClose: () => void;
  /** Aba inicial no modo edit (PR5c usa 'permissoes' pós-criação). */
  initialTab?: AbaEdit;
  /** PR5c — chamado após criação bem-sucedida (pai reabre em modo edit/permissões). */
  onUserCreated?: (usuario: Usuario) => void;
}

interface PapelCard {
  valor: 'ADMIN' | 'USER' | null; // null = stub RBAC
  nome: string;
  descricao: string;
}

// Papéis reais (TUsuario.Perfil) + stubs do RBAC futuro (mock: Compras/Conferência/Fiscal).
const PAPEIS: PapelCard[] = [
  { valor: 'ADMIN', nome: 'Administrador', descricao: 'Acesso total ao sistema' },
  { valor: 'USER', nome: 'Usuário', descricao: 'Acesso conforme o perfil de acesso (aba ao lado)' },
  { valor: null, nome: 'Compras', descricao: 'Reposição, cotações, pedidos e metas' },
  { valor: null, nome: 'Conferência', descricao: 'Conferência de NF-e e recebimento' },
  { valor: null, nome: 'Fiscal', descricao: 'NF-e / CT-e de terceiros' },
];

function iniciaisDe(nome: string): string {
  const partes = (nome || '?').trim().split(/\s+/);
  if (partes.length === 1) return partes[0].slice(0, 2).toUpperCase();
  return (partes[0][0] + partes[partes.length - 1][0]).toUpperCase();
}

const EMAIL_RE = /\S+@\S+\.\S+/;
const SENHA_RE = /^(?=.*[A-Za-z])(?=.*\d).+$/;

const UserDialog: React.FC<UserDialogProps> = ({
  mode,
  usuario,
  isOpen,
  onClose,
  initialTab = 'dados',
  onUserCreated,
}) => {
  const toast = useToast();
  const { isAdmin: isAdminLogado } = usePermissoes();
  const criar = useCriarUsuario();
  const atualizar = useAtualizarUsuario();
  const alterarStatus = useAlterarStatus();
  const redefinirSenha = useRedefinirSenha();
  const alterarSenhaManual = useAlterarSenhaManual();

  const [abaAtiva, setAbaAtiva] = useState<AbaEdit>('dados');
  const [nome, setNome] = useState('');
  const [email, setEmail] = useState('');
  const [fone, setFone] = useState('');
  const [login, setLogin] = useState('');
  const [senha, setSenha] = useState('');
  const [showSenha, setShowSenha] = useState(false);
  const [perfil, setPerfil] = useState<'ADMIN' | 'USER'>('USER');
  const [ativo, setAtivo] = useState(true);
  const [erroSubmit, setErroSubmit] = useState<string | null>(null);

  // Aba Segurança
  const [novaSenhaManual, setNovaSenhaManual] = useState('');
  const [confirmarSenhaManual, setConfirmarSenhaManual] = useState('');
  const [confirmReset, setConfirmReset] = useState(false);
  const [senhaGerada, setSenhaGerada] = useState<string | null>(null);
  const [senhaCopiada, setSenhaCopiada] = useState(false);

  const dialogRef = useRef<HTMLDivElement>(null);
  const senhaDialogRef = useRef<HTMLDivElement>(null);
  const nomeRef = useRef<HTMLInputElement>(null);

  // Reidrata o form a cada abertura (novo = vazio; editar = dados da linha).
  useEffect(() => {
    if (!isOpen) return;
    setAbaAtiva(mode === 'edit' ? initialTab : 'dados');
    setNome(mode === 'edit' ? usuario?.nome ?? '' : '');
    setEmail(mode === 'edit' ? usuario?.email ?? '' : '');
    setFone(mode === 'edit' ? maskTelefoneInput(usuario?.fone ?? '') : '');
    setLogin(mode === 'edit' ? usuario?.login ?? '' : '');
    setSenha('');
    setShowSenha(false);
    setPerfil(mode === 'edit' && usuario?.perfil === 'ADMIN' ? 'ADMIN' : 'USER');
    setAtivo(mode === 'edit' ? usuario?.ativo ?? true : true);
    setErroSubmit(null);
    setNovaSenhaManual('');
    setConfirmarSenhaManual('');
    setConfirmReset(false);
    setSenhaGerada(null);
    setTimeout(() => nomeRef.current?.focus(), 0);
  }, [isOpen, mode, usuario, initialTab]);

  // Trap do diálogo de senha gerada (z-60) tem prioridade sobre o do pai.
  useFocusTrap(dialogRef, isOpen && !senhaGerada, onClose);
  useFocusTrap(senhaDialogRef, !!senhaGerada, () => setSenhaGerada(null));

  const emailValido = EMAIL_RE.test(email);
  const senhaValida = senha.length >= 10 && SENHA_RE.test(senha);
  const podeSalvar = useMemo(() => {
    if (!nome.trim() || !emailValido) return false;
    if (mode === 'new') return login.trim().length >= 3 && senhaValida;
    return true;
  }, [nome, emailValido, mode, login, senhaValida]);

  const salvando = criar.isPending || atualizar.isPending || alterarStatus.isPending;

  if (!isOpen) return null;
  if (mode === 'edit' && !usuario) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!podeSalvar || salvando) return;
    setErroSubmit(null);

    if (mode === 'new') {
      criar.mutate(
        { nome: nome.trim(), login: login.trim(), email: email.trim(), senha, perfil, ativo: true },
        {
          onSuccess: (response) => {
            const criado = response?.data?.data;
            toast.sucesso(`Usuário ${nome.trim()} criado com sucesso.`);
            if (criado && onUserCreated) onUserCreated(criado);
            onClose();
          },
          onError: (error: unknown) => {
            logError('Erro ao criar usuário:', error);
            setErroSubmit(extrairMensagemErro(error, 'Erro ao criar usuário. Tente novamente.'));
          },
        }
      );
      return;
    }

    // Editar: PUT (nome/email/fone/perfil) + PATCH status se o toggle mudou.
    try {
      await atualizar.mutateAsync({
        id: usuario!.id,
        data: { nome: nome.trim(), email: email.trim(), fone: unmaskDigits(fone), perfil },
      });
      if (ativo !== usuario!.ativo) {
        await alterarStatus.mutateAsync({ id: usuario!.id, data: { ativo } });
      }
      toast.sucesso('Usuário atualizado.');
      onClose();
    } catch (error: unknown) {
      logError('Erro ao atualizar usuário:', error);
      setErroSubmit(extrairMensagemErro(error, 'Erro ao salvar alterações. Tente novamente.'));
    }
  };

  const gerarSenhaNovo = () => {
    const s = gerarSenhaSegura(12);
    setSenha(s);
    setShowSenha(true);
  };

  // Segurança — redefinir com senha gerada (CSPRNG) + diálogo de cópia.
  const handleResetSenha = () => {
    if (!usuario) return;
    const nova = gerarSenhaSegura(12);
    redefinirSenha.mutate(
      { id: usuario.id, data: { novaSenha: nova } },
      {
        onSuccess: () => {
          setConfirmReset(false);
          setSenhaCopiada(false);
          setSenhaGerada(nova);
        },
        onError: (error: unknown) => {
          logError('Erro ao redefinir senha:', error);
          toast.erro(extrairMensagemErro(error, 'Erro ao redefinir senha.'));
        },
      }
    );
  };

  const senhaManualValida = novaSenhaManual.length >= 10 && SENHA_RE.test(novaSenhaManual);
  const handleSenhaManual = (e: React.FormEvent) => {
    e.preventDefault();
    if (!usuario || !senhaManualValida || novaSenhaManual !== confirmarSenhaManual) return;
    alterarSenhaManual.mutate(
      { id: usuario.id, data: { novaSenha: novaSenhaManual } },
      {
        onSuccess: () => {
          setNovaSenhaManual('');
          setConfirmarSenhaManual('');
          toast.sucesso('Senha alterada com sucesso!');
        },
        onError: (error: unknown) => {
          logError('Erro ao alterar senha:', error);
          toast.erro(extrairMensagemErro(error, 'Erro ao alterar senha.'));
        },
      }
    );
  };

  const mostrarFooterSalvar = mode === 'new' || abaAtiva === 'dados';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[var(--scrim)] backdrop-blur-[2px]"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={mode === 'new' ? 'Novo usuário' : 'Editar usuário'}
        className={`flex w-full max-h-[90vh] flex-col bg-surface border border-line-strong rounded-iris-lg shadow-iris-xl ${
          mode === 'new' ? 'max-w-[480px]' : 'max-w-[560px]'
        }`}
      >
        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-line shrink-0">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-accent-soft text-accent-text">
            {mode === 'new' ? (
              <PlusIcon className="h-5 w-5" />
            ) : (
              <span className="text-[13px] font-semibold">{iniciaisDe(usuario!.nome)}</span>
            )}
          </span>
          <div className="flex-1 min-w-0">
            <h2 className="text-base font-semibold text-tx tracking-[-0.01em]">
              {mode === 'new' ? 'Novo usuário' : 'Editar usuário'}
            </h2>
            <p className="text-[13px] text-tx-3 truncate">
              {mode === 'new' ? 'Adicione um membro à organização.' : <>@{usuario!.login} · atualize dados, perfil de acesso e segurança.</>}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar"
            className="p-1.5 rounded-iris-sm text-tx-3 hover:bg-surface-2 hover:text-tx transition-colors"
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
        </div>

        {/* Abas (só edição) */}
        {mode === 'edit' && (
          <div className="px-5 pt-1 shrink-0">
            <AbasSublinhado<AbaEdit>
              abas={[
                { key: 'dados', label: 'Dados', icon: <UserIcon className="w-4 h-4" /> },
                { key: 'permissoes', label: 'Perfil de acesso', icon: <ShieldCheckIcon className="w-4 h-4" /> },
                // PR2 — endpoints de senha são AdminPolicy; esconde de não-ADMIN.
                ...(isAdminLogado ? [{ key: 'seguranca' as const, label: 'Segurança', icon: <LockClosedIcon className="w-4 h-4" /> }] : []),
              ]}
              ativa={abaAtiva}
              onChange={setAbaAtiva}
            />
          </div>
        )}

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5">
          {(mode === 'new' || abaAtiva === 'dados') && (
            <form id="user-dialog-form" onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="ud-nome" className="block text-xs font-medium text-tx-2 mb-1">Nome completo *</label>
                <input
                  ref={nomeRef}
                  id="ud-nome"
                  type="text"
                  value={nome}
                  onChange={(e) => setNome(e.target.value)}
                  placeholder="Ex.: Ana Paula Souza"
                  className={`w-full ${inputIris}`}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label htmlFor="ud-email" className="block text-xs font-medium text-tx-2 mb-1">Email *</label>
                  <input
                    id="ud-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="nome@empresa.com.br"
                    className={`w-full ${inputIris} ${email && !emailValido ? '!border-danger' : ''}`}
                  />
                  {email && !emailValido && <p className="text-xs text-danger mt-1">Email inválido.</p>}
                </div>
                {mode === 'edit' ? (
                  <div>
                    <label htmlFor="ud-fone" className="block text-xs font-medium text-tx-2 mb-1">Telefone</label>
                    <input
                      id="ud-fone"
                      type="tel"
                      value={fone}
                      onChange={(e) => setFone(maskTelefoneInput(e.target.value))}
                      placeholder="(11) 99999-9999"
                      className={`w-full ${inputIris}`}
                    />
                  </div>
                ) : (
                  <div>
                    <label htmlFor="ud-login" className="block text-xs font-medium text-tx-2 mb-1">Login *</label>
                    <input
                      id="ud-login"
                      type="text"
                      value={login}
                      onChange={(e) => setLogin(e.target.value)}
                      placeholder="ana.souza"
                      className={`w-full font-mono ${inputIris}`}
                    />
                  </div>
                )}
              </div>

              {mode === 'new' && (
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label htmlFor="ud-senha" className="block text-xs font-medium text-tx-2">Senha inicial *</label>
                    <button type="button" onClick={gerarSenhaNovo} className="text-xs font-medium text-accent-text hover:underline">
                      Gerar
                    </button>
                  </div>
                  <div className="relative">
                    <input
                      id="ud-senha"
                      type={showSenha ? 'text' : 'password'}
                      value={senha}
                      onChange={(e) => setSenha(e.target.value)}
                      placeholder="10+ caracteres, com letra e número"
                      className={`w-full pr-9 font-mono ${inputIris} ${senha && !senhaValida ? '!border-danger' : ''}`}
                    />
                    <button
                      type="button"
                      onClick={() => setShowSenha(v => !v)}
                      aria-label={showSenha ? 'Ocultar senha' : 'Mostrar senha'}
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 text-tx-3 hover:text-tx"
                    >
                      {showSenha ? <EyeSlashIcon className="h-4 w-4" /> : <EyeIcon className="h-4 w-4" />}
                    </button>
                  </div>
                  {senha && !senhaValida && (
                    <p className="text-xs text-danger mt-1">Mínimo 10 caracteres, com letra e número.</p>
                  )}
                </div>
              )}

              {/* Papel de acesso — ADMIN|USER reais; Compras/Conferência/Fiscal = RBAC futuro */}
              <div>
                <div className="block text-xs font-medium text-tx-2 mb-1.5">Papel de acesso</div>
                <div className="space-y-2" role="radiogroup" aria-label="Papel de acesso">
                  {PAPEIS.map((p) => {
                    const stub = p.valor === null;
                    // Só ADMIN logado promove a ADMIN (backend devolve 403 também).
                    const bloqueado = p.valor === 'ADMIN' && !isAdminLogado;
                    const selecionado = !stub && perfil === p.valor;
                    return (
                      <button
                        key={p.nome}
                        type="button"
                        role="radio"
                        aria-checked={selecionado}
                        disabled={stub || bloqueado}
                        onClick={() => p.valor && setPerfil(p.valor)}
                        className={`w-full flex items-center gap-3 px-3 py-2.5 text-left rounded-iris border transition-colors ${
                          selecionado
                            ? 'border-accent bg-accent-soft'
                            : 'border-line bg-surface hover:bg-surface-2'
                        } ${stub || bloqueado ? 'opacity-60 cursor-not-allowed hover:bg-surface' : ''}`}
                      >
                        <span
                          className={`flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full border-2 ${
                            selecionado ? 'border-accent' : 'border-line-strong'
                          }`}
                        >
                          {selecionado && <span className="h-2 w-2 rounded-full bg-accent" />}
                        </span>
                        <span className="flex-1 min-w-0">
                          <span className={`flex items-center gap-2 text-sm font-semibold ${selecionado ? 'text-accent-text' : 'text-tx'}`}>
                            {p.nome}
                            {/* TODO(backend): papéis RBAC granulares — SISTEMA_PLANEJAMENTO.md */}
                            {stub && <BadgeEmBreve />}
                          </span>
                          <span className="block text-xs text-tx-3">{p.descricao}</span>
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Só Editar — ativar/suspender (real, PATCH /status no salvar) */}
              {mode === 'edit' && (
                <div className="flex items-center gap-4 pt-3 border-t border-line">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-tx">Usuário ativo</div>
                    <div className="text-[13px] text-tx-3">{ativo ? 'Pode acessar o sistema.' : 'Acesso suspenso.'}</div>
                  </div>
                  <ToggleIris ativo={ativo} onChange={setAtivo} aria-label="Usuário ativo" />
                </div>
              )}

              {/* Só Novo — convite por email ainda não existe (stub) */}
              {mode === 'new' && (
                <div className="flex items-start gap-2.5 rounded-iris bg-accent-soft px-3 py-2.5">
                  <BellIcon className="h-4 w-4 text-accent-text shrink-0 mt-0.5" />
                  <p className="text-[13px] text-tx">
                    {/* TODO(backend): convite por email com link pra definir senha + assentos do plano */}
                    <span className="font-semibold text-accent-text">Convite por email</span> <BadgeEmBreve className="align-middle" /> —
                    por enquanto, defina login e senha iniciais e compartilhe com o usuário.
                  </p>
                </div>
              )}

              {erroSubmit && (
                <div className="rounded-iris border border-danger-border bg-danger-soft px-3 py-2 text-xs text-danger">
                  {erroSubmit}
                </div>
              )}
            </form>
          )}

          {/* Aba Perfil de acesso — permissões reais por usuário + modelos de perfil */}
          {mode === 'edit' && abaAtiva === 'permissoes' && (
            <PermissoesEditor usuarioId={usuario!.id} perfil={perfil} />
          )}

          {/* Aba Segurança (só ADMIN) */}
          {mode === 'edit' && abaAtiva === 'seguranca' && isAdminLogado && (
            <div className="space-y-4">
              {/* Redefinir com senha gerada */}
              <div className="rounded-iris border border-line p-4">
                <div className="flex items-start gap-3">
                  <KeyIcon className="h-5 w-5 text-tx-3 mt-0.5 shrink-0" />
                  <div className="flex-1">
                    <h3 className="text-sm font-semibold text-tx flex items-center gap-2">
                      Redefinir senha
                    </h3>
                    {/* TODO(backend): envio por email não existe — a senha gerada só
                        aparece no diálogo de cópia abaixo (não prometer email). */}
                    <p className="text-[13px] text-tx-3 mt-0.5">
                      Gera uma senha forte pra você copiar e repassar ao usuário.
                      Envio automático por email <BadgeEmBreve className="align-middle" />.
                    </p>
                    {!confirmReset ? (
                      <button type="button" onClick={() => setConfirmReset(true)} className={`${btnSecundario} h-8 px-3 text-xs mt-3`}>
                        Gerar nova senha
                      </button>
                    ) : (
                      <div className="flex gap-2 mt-3">
                        <button type="button" onClick={() => setConfirmReset(false)} className={`${btnSecundario} h-8 px-3 text-xs`}>
                          Cancelar
                        </button>
                        <button
                          type="button"
                          onClick={handleResetSenha}
                          disabled={redefinirSenha.isPending}
                          className={`${btnPrimario} h-8 px-3 text-xs`}
                        >
                          {redefinirSenha.isPending && <ArrowPathIcon className="w-3.5 h-3.5 animate-spin" />}
                          {redefinirSenha.isPending ? 'Gerando…' : 'Confirmar'}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Definir senha manualmente */}
              <div className="rounded-iris border border-line p-4">
                <div className="flex items-start gap-3">
                  <LockClosedIcon className="h-5 w-5 text-tx-3 mt-0.5 shrink-0" />
                  <div className="flex-1">
                    <h3 className="text-sm font-semibold text-tx">Definir nova senha</h3>
                    <p className="text-[13px] text-tx-3 mt-0.5">Digite uma senha específica pro usuário.</p>
                    <form onSubmit={handleSenhaManual} className="mt-3 space-y-2.5">
                      <div>
                        <input
                          type="password"
                          placeholder="Nova senha (10+ com letra e número)"
                          value={novaSenhaManual}
                          onChange={(e) => setNovaSenhaManual(e.target.value)}
                          autoComplete="new-password"
                          className={`w-full ${inputIris} ${novaSenhaManual && !senhaManualValida ? '!border-danger' : ''}`}
                        />
                        {novaSenhaManual && !senhaManualValida && (
                          <p className="text-xs text-danger mt-1">Mínimo 10 caracteres, com letra e número.</p>
                        )}
                      </div>
                      <div>
                        <input
                          type="password"
                          placeholder="Confirmar nova senha"
                          value={confirmarSenhaManual}
                          onChange={(e) => setConfirmarSenhaManual(e.target.value)}
                          autoComplete="new-password"
                          className={`w-full ${inputIris} ${confirmarSenhaManual && confirmarSenhaManual !== novaSenhaManual ? '!border-danger' : ''}`}
                        />
                        {confirmarSenhaManual && confirmarSenhaManual !== novaSenhaManual && (
                          <p className="text-xs text-danger mt-1">As senhas não coincidem.</p>
                        )}
                      </div>
                      <button
                        type="submit"
                        disabled={alterarSenhaManual.isPending || !senhaManualValida || novaSenhaManual !== confirmarSenhaManual}
                        className={`${btnPrimario} h-8 px-3 text-xs`}
                      >
                        {alterarSenhaManual.isPending ? 'Alterando…' : 'Alterar senha'}
                      </button>
                    </form>
                  </div>
                </div>
              </div>

              {/* Informações */}
              <div className="rounded-iris border border-line p-4 space-y-2">
                <h3 className="text-sm font-semibold text-tx">Informações</h3>
                <div className="flex justify-between text-[13px]">
                  <span className="text-tx-3">Último login</span>
                  <span className="text-tx font-mono">{formatDateTime(usuario!.ultimoLogin, 'Nunca')}</span>
                </div>
                <div className="flex justify-between text-[13px]">
                  <span className="text-tx-3">Criado em</span>
                  <span className="text-tx font-mono">{formatDateTime(usuario!.dataCriacao, '—')}</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-2 px-5 py-3.5 border-t border-line shrink-0">
          <div>
            {mode === 'edit' && (
              // TODO(backend): não existe DELETE /usuarios — remoção definitiva fica
              // pra quando houver; suspensão (toggle na aba Dados) cobre o bloqueio.
              <button
                type="button"
                disabled
                title="Remoção definitiva em breve — use o toggle pra suspender o acesso"
                className="inline-flex items-center gap-1.5 text-sm font-medium text-danger opacity-50 cursor-not-allowed"
              >
                <TrashIcon className="h-4 w-4" /> Remover <BadgeEmBreve />
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button type="button" onClick={onClose} disabled={salvando} className={btnSecundario}>
              {mostrarFooterSalvar ? 'Cancelar' : 'Fechar'}
            </button>
            {mostrarFooterSalvar && (
              <button
                type="submit"
                form="user-dialog-form"
                disabled={!podeSalvar || salvando}
                className={btnPrimario}
              >
                {salvando ? 'Salvando…' : mode === 'new' ? 'Criar usuário' : 'Salvar alterações'}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Diálogo de senha gerada (UX_AUDIT R6) — z-[60], acima do pai. */}
      {senhaGerada && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="senha-gerada-titulo"
          className="fixed inset-0 z-[60] flex items-center justify-center bg-[var(--scrim)] p-4"
          onClick={(e) => { if (e.target === e.currentTarget) setSenhaGerada(null); }}
        >
          <div ref={senhaDialogRef} className="w-full max-w-md rounded-iris-lg border border-line-strong bg-surface shadow-iris-xl">
            <div className="flex items-start gap-2 border-b border-line px-4 py-3">
              <KeyIcon className="mt-0.5 h-5 w-5 shrink-0 text-accent-text" aria-hidden />
              <h2 id="senha-gerada-titulo" className="text-sm font-semibold text-tx">Nova senha gerada</h2>
            </div>
            <div className="space-y-3 p-4">
              <div className="flex items-start gap-2 rounded-iris border border-amber-400/30 bg-amber-400/10 px-3 py-2 text-xs text-amber-700 dark:text-amber-300">
                <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                <span>Anote ou copie agora. Esta senha não será exibida novamente.</span>
              </div>
              <div className="flex items-stretch gap-2">
                <input
                  readOnly
                  value={senhaGerada}
                  className={`flex-1 font-mono ${inputIris}`}
                  onFocus={(e) => e.currentTarget.select()}
                />
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(senhaGerada);
                      setSenhaCopiada(true);
                      setTimeout(() => setSenhaCopiada(false), 2000);
                    } catch {
                      toast.erro('Não foi possível copiar — selecione e copie manualmente.');
                    }
                  }}
                  className={`${btnPrimario} h-9 px-3 text-xs`}
                >
                  {senhaCopiada ? <CheckIcon className="h-3.5 w-3.5" /> : <DocumentDuplicateIcon className="h-3.5 w-3.5" />}
                  {senhaCopiada ? 'Copiado!' : 'Copiar'}
                </button>
              </div>
              {usuario && (
                <p className="text-xs text-tx-3">
                  Usuário: <span className="font-medium text-tx">{usuario.login}</span>
                </p>
              )}
            </div>
            <div className="flex items-center justify-end border-t border-line px-4 py-3">
              <button type="button" onClick={() => setSenhaGerada(null)} className={btnSecundario}>
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserDialog;
